(function () {
  if (customElements.get('wd-customer-gallery')) return;

  const THUMB_WINDOW_RADIUS = 20;
  const THUMB_WINDOW_THRESHOLD = 60;
  const PRELOAD_AHEAD = 2;

  class WDCustomerGallery extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-gallery-grid]');
      this.lightbox = this.querySelector('[data-gallery-lightbox]');
      this.loadMoreRoot = this.querySelector('[data-gallery-load-more]');
      this.loadMoreBtn = this.querySelector('[data-gallery-load-more-btn]');
      this.loadMoreStatus = this.querySelector('[data-gallery-load-more-status]');

      if (!this.lightbox) return;

      this.lightboxImageA = this.lightbox.querySelector('[data-lightbox-image-a]');
      this.lightboxImageB = this.lightbox.querySelector('[data-lightbox-image-b]');
      this.lightboxImage = this.lightboxImageA;
      this.lightboxImageAlt = this.lightboxImageB;
      this.activeImageSlot = 'a';
      this.lightboxCaption = this.lightbox.querySelector('[data-lightbox-caption]');
      this.lightboxCounterCurrent = this.lightbox.querySelector('[data-lightbox-counter-current]');
      this.lightboxCounterTotal = this.lightbox.querySelector('[data-lightbox-counter-total]');
      this.lightboxPrev = this.lightbox.querySelectorAll('[data-lightbox-prev]');
      this.lightboxNext = this.lightbox.querySelectorAll('[data-lightbox-next]');
      this.lightboxBackdrop = this.lightbox.querySelector('.wd-gallery-lightbox__backdrop');
      this.lightboxShell = this.lightbox.querySelector('.wd-gallery-lightbox__shell');
      this.lightboxClose = this.lightbox.querySelectorAll('[data-lightbox-close]');
      this.lightboxThumbs = this.lightbox.querySelector('[data-lightbox-thumbs]');
      this.lightboxThumbsTrack = this.lightbox.querySelector('[data-lightbox-thumbs-track]');
      this.lightboxFrame = this.lightbox.querySelector('[data-lightbox-frame]');
      this.lightboxImageWrap = this.lightbox.querySelector('.wd-gallery-lightbox__image-wrap');

      this.filtersRoot = this.querySelector('[data-gallery-filters]');

      this.sectionId =
        this.dataset.sectionId ||
        this.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '') ||
        '';
      this.batchSize = Number(this.dataset.batchSize || 50);
      this.totalCount = Number(this.dataset.totalCount || 0);
      this.serverPageSize = Number(this.dataset.serverPageSize || 250);
      this.totalServerPages = Number(this.dataset.totalServerPages || 1);
      this.contentSource = this.dataset.contentSource || 'metaobjects';
      this.lowestFetchedPage = Number(this.loadMoreRoot?.dataset.currentServerPage || 1);
      this.knownPhotoIds = new Set();
      this.visibleCount = 0;
      this.filterBatchSize = 15;
      this.filterVisibleCount = this.filterBatchSize;
      this._filterToken = 0;
      this.isLoadingMore = false;

      this.currentIndex = 0;
      this.activeFilter = 'all';
      this.touchStartX = 0;
      this.triggers = [];
      this.thumbButtons = [];
      this.imageZoomScale = 1.85;
      this.imagePanX = 0;
      this.imagePanY = 0;
      this.isImageZoomed = false;
      this.isPanningImage = false;
      this.canImageZoom = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.didPanImage = false;
      this.preloadCache = new Map();
      this._visibleList = [];
      this._useWindowedThumbs = false;
      this._thumbWindowStart = 0;
      this._thumbWindowEnd = 0;

      if (this.grid) {
        this.initGridPreload();
        this.registerKnownIds(this.galleryItems());
        this.sortItems();
        this.applyInitialBatch();
        this.collectTriggers();
        this.buildFilters();
        this.initImageLoading();
        this.initReveal();
      }

      this.bindEvents();
      this.initImageZoom();
      this.initLoadMore();
      this.initStickyOffset();
    }

    // The filter bar is sticky (top: 0) but sits below the theme header in
    // z-order, so it would slide underneath the sticky header. Track the
    // header's current bottom edge and expose it as --wd-sticky-offset so the
    // bar pins just below the header, whether the header is visible or hidden.
    initStickyOffset() {
      const filters = this.querySelector('.wd-gallery__filters-wrap');
      if (!filters) return;

      const header =
        document.querySelector('.shopify-section-header') ||
        document.querySelector('.section-header') ||
        document.querySelector('sticky-header')?.closest('.shopify-section');
      if (!header) return;

      let rafId = 0;
      const update = () => {
        rafId = 0;
        const bottom = header.getBoundingClientRect().bottom;
        const offset = Math.max(0, Math.round(bottom));
        this.style.setProperty('--wd-sticky-offset', `${offset}px`);
      };
      const requestUpdate = () => {
        if (!rafId) rafId = requestAnimationFrame(update);
      };

      update();
      this._onStickyOffsetChange = requestUpdate;
      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate);
    }

    initCtaReveal() {
      const targets = [...this.querySelectorAll('[data-gallery-cta]')];
      if (!targets.length) return;

      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReduced) {
        targets.forEach((target) => target.classList.add('is-revealed'));
        return;
      }

      if (!this._revealObserver) {
        this._revealObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-revealed');
              this._revealObserver.unobserve(entry.target);
            });
          },
          { threshold: 0.08, rootMargin: '0px 0px 80px 0px' }
        );
      }

      targets.forEach((target) => {
        if (!target.classList.contains('is-revealed')) {
          this._revealObserver.observe(target);
        }
      });
    }

    getPhotoId(item) {
      const explicitId = item?.dataset?.photoId;
      if (explicitId) return String(explicitId);

      const trigger = item?.querySelector('[data-gallery-open]');
      const fullSrc = trigger?.dataset?.fullSrc;
      if (fullSrc) return this.normalizePhotoSrc(fullSrc);

      const img = trigger?.querySelector('img');
      if (img?.currentSrc) return this.normalizePhotoSrc(img.currentSrc);
      if (img?.src) return this.normalizePhotoSrc(img.src);

      const index = item?.dataset?.index;
      if (index !== undefined) return `index-${index}`;

      return null;
    }

    normalizePhotoSrc(src) {
      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.delete('width');
        url.searchParams.delete('height');
        url.searchParams.delete('crop');
        return url.pathname + url.search;
      } catch (error) {
        return String(src);
      }
    }

    registerKnownIds(items) {
      items.forEach((item) => {
        const photoId = this.getPhotoId(item);
        if (photoId) this.knownPhotoIds.add(photoId);
      });
    }

    animateNewItems(items) {
      if (!items.length) return;

      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReduced) {
        items.forEach((item) => {
          item.classList.add('is-revealed');
          item.classList.remove('is-reveal-pending', 'is-batch-enter');
        });
        return;
      }

      // Stagger by column count so each visual row eases in just after the one
      // above it, which reads as a smooth cascade rather than a single jump.
      const columns = this.getColumnCount();

      items.forEach((item, index) => {
        const row = Math.floor(index / columns);
        item.classList.add('is-reveal-pending', 'is-batch-enter');
        item.classList.remove('is-revealed');
        item.style.setProperty('--reveal-index', String(row));
      });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          items.forEach((item) => item.classList.add('is-revealed'));
        });
      });

      const rows = Math.ceil(items.length / columns);
      const cleanupDelay = 700 + rows * 60;
      window.clearTimeout(this._batchCleanupTimer);
      this._batchCleanupTimer = window.setTimeout(() => {
        items.forEach((item) => {
          item.classList.remove('is-batch-enter');
          item.style.removeProperty('--reveal-index');
        });
      }, cleanupDelay);
    }

    getColumnCount() {
      const width = window.innerWidth;
      const desktopCols = Number(getComputedStyle(this).getPropertyValue('--wd-gallery-cols')) || 3;
      const tabletCols = Number(getComputedStyle(this).getPropertyValue('--wd-gallery-cols-tab')) || 2;
      if (width <= 749) return 2; // matches the fixed 2-per-row mobile CSS grid
      if (width <= 989) return tabletCols;
      return desktopCols;
    }

    revealItemsInstantly(items) {
      items.forEach((item) => {
        item.classList.add('is-revealed');
        item.classList.remove('is-reveal-pending', 'is-batch-enter');
      });
    }

    // Order the grid newest-first by upload timestamp. Items without date
    // information keep their existing DOM position (the sort is stable), which
    // already reflects newest-first server order. data-index is NOT used as a
    // tiebreaker because it restarts on every server page and would interleave
    // photos from different pages.
    sortItems() {
      if (!this.grid) return;

      const items = [...this.grid.querySelectorAll('[data-gallery-item]')];
      items
        .sort((a, b) => {
          const timeA = Number(a.dataset.timestamp || 0);
          const timeB = Number(b.dataset.timestamp || 0);
          return timeB - timeA;
        })
        .forEach((item) => this.grid.appendChild(item));
    }

    galleryItems() {
      return [...this.grid.querySelectorAll('[data-gallery-item]')];
    }

    applyInitialBatch() {
      const items = this.galleryItems();
      this.visibleCount = Math.min(this.batchSize, items.length);

      items.forEach((item, index) => {
        const shouldShow = index < this.visibleCount;
        if (shouldShow) {
          item.removeAttribute('hidden');
          item.removeAttribute('data-gallery-deferred');
          item.classList.remove('wd-gallery__item--deferred');
        } else {
          item.setAttribute('hidden', '');
          item.setAttribute('data-gallery-deferred', '');
          item.classList.add('wd-gallery__item--deferred');
        }
      });
    }

    collectTriggers() {
      this.triggers = [...this.grid.querySelectorAll('[data-gallery-open]')];
      this.visibleTriggers = () =>
        this.triggers.filter((trigger) => {
          const item = trigger.closest('[data-gallery-item]');
          return item && !item.classList.contains('is-hidden') && !item.hasAttribute('hidden');
        });
    }

    // Photos the lightbox can navigate: every loaded item that matches the
    // active filter, including ones not yet revealed in the grid (deferred).
    lightboxTriggers() {
      return [...this.grid.querySelectorAll('[data-gallery-open]')].filter((trigger) => {
        const item = trigger.closest('[data-gallery-item]');
        return item && !item.classList.contains('is-hidden');
      });
    }

    buildFilters() {
      if (!this.filtersRoot) return;
      this.refreshFilters(true);
    }

    refreshFilters(isInitial = false) {
      if (!this.filtersRoot) return;

      const existing = new Set(
        [...this.filtersRoot.querySelectorAll('[data-filter]')].map((button) => button.dataset.filter)
      );

      this.grid.querySelectorAll('[data-gallery-item][data-room-label]').forEach((item) => {
        const label = item.dataset.roomLabel;
        const handle = item.dataset.room;
        if (!label || !handle || existing.has(handle)) return;

        existing.add(handle);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'wd-gallery__filter';
        button.dataset.filter = handle;
        button.textContent = label;
        this.filtersRoot.appendChild(button);
      });

      const filterCount = existing.size - (existing.has('all') ? 1 : 0);
      if (isInitial && filterCount < 2) {
        this.filtersRoot.hidden = true;
      } else if (filterCount >= 1) {
        this.filtersRoot.hidden = false;
      }
    }

    initReveal() {
      const targets = [
        ...(this.grid ? this.getRevealTargets(this.grid) : []),
        ...this.querySelectorAll('[data-gallery-cta]'),
      ];

      if (!targets.length) return;

      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReduced) {
        targets.forEach((target) => target.classList.add('is-revealed'));
        return;
      }

      this._revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            this._revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.08, rootMargin: '0px 0px 80px 0px' }
      );

      targets.forEach((target) => this._revealObserver.observe(target));
    }

    getRevealTargets(scope) {
      return [...scope.querySelectorAll('[data-gallery-item].is-reveal-pending:not([hidden])')];
    }

    observeReveal(targets) {
      if (!targets.length) return;

      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReduced) {
        targets.forEach((target) => target.classList.add('is-revealed'));
        return;
      }

      if (!this._revealObserver) {
        this.initReveal();
        return;
      }

      targets.forEach((target) => this._revealObserver.observe(target));
    }

    initImageLoading(scope) {
      const root = scope || this.grid;
      if (!root) return;

      const mediaElements = root.querySelectorAll('.wd-gallery__media:not([data-media-init])');
      if (!mediaElements.length) return;

      const markLoaded = (media) => {
        media.classList.remove('is-loading');
        media.classList.add('is-loaded');
        media.setAttribute('data-media-init', '1');
      };

      if ('IntersectionObserver' in window) {
        if (!this._mediaObserver) {
          this._mediaObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                const media = entry.target;
                const img = media.querySelector('.wd-gallery__image');
                if (!img) return;

                if (img.complete && img.naturalWidth > 0) {
                  markLoaded(media);
                } else {
                  img.addEventListener('load', () => markLoaded(media), { once: true });
                  img.addEventListener('error', () => markLoaded(media), { once: true });
                }

                this._mediaObserver.unobserve(media);
              });
            },
            { rootMargin: '200px 0px' }
          );
        }

        mediaElements.forEach((media) => this._mediaObserver.observe(media));
        return;
      }

      mediaElements.forEach((media) => {
        const img = media.querySelector('.wd-gallery__image');
        if (!img) return;

        if (img.complete && img.naturalWidth > 0) {
          markLoaded(media);
          return;
        }

        img.addEventListener('load', () => markLoaded(media), { once: true });
        img.addEventListener('error', () => markLoaded(media), { once: true });
      });
    }

    initGridPreload() {
      if (!this.grid) return;

      const preloadOnIntent = (event) => {
        const trigger = event.target.closest('[data-gallery-open]');
        if (!trigger) return;
        this.preloadImage(trigger.dataset.fullSrc);
      };

      this.grid.addEventListener('pointerenter', preloadOnIntent, true);
      this.grid.addEventListener('focusin', preloadOnIntent);
    }

    initLoadMore() {
      if (!this.loadMoreBtn || !this.loadMoreRoot) return;

      this.updateLoadMoreState();

      this.loadMoreBtn.addEventListener('click', (event) => {
        event.preventDefault();
        this.loadMore();
      });
    }

    setLoadMoreLoading(isLoading) {
      this.isLoadingMore = isLoading;
      this.loadMoreRoot?.classList.toggle('is-loading', isLoading);
      if (this.loadMoreBtn) {
        this.loadMoreBtn.disabled = isLoading;
      }
    }

    hasUnfetchedPages() {
      return this.contentSource === 'metaobjects' && this.lowestFetchedPage > 1;
    }

    updateLoadMoreState() {
      if (!this.loadMoreRoot) return;

      let hasMore;
      if (this.activeFilter === 'all') {
        const total = this.galleryItems().length;
        hasMore = this.visibleCount < total || this.hasUnfetchedPages();
      } else {
        const matching = this.matchingItems().length;
        hasMore = this.filterVisibleCount < matching || this.hasUnfetchedPages();
      }

      this.loadMoreRoot.hidden = !hasMore;
      this.loadMoreRoot.dataset.hasMore = hasMore ? 'true' : 'false';
    }

    async loadMore() {
      if (this.isLoadingMore || !this.grid) return;

      this.setLoadMoreLoading(true);
      this.grid.classList.add('is-loading-batch');

      try {
        if (this.activeFilter === 'all') {
          // Unfiltered: grow the visible window by one batch, pulling older
          // server pages first if the page doesn't hold enough items yet.
          const target = this.visibleCount + this.batchSize;
          let available = this.galleryItems().length;
          while (available < target && this.hasUnfetchedPages()) {
            const added = await this.fetchOlderPage();
            if (!added) break;
            available = this.galleryItems().length;
          }
          this.visibleCount = Math.min(target, available);
        } else {
          // Filtered: reveal the next group of matching photos only, fetching
          // older pages until enough matching photos exist (or pages run out).
          const target = this.filterVisibleCount + this.filterBatchSize;
          await this.ensureMatchingLoaded(target);
          this.filterVisibleCount = Math.min(target, this.matchingItems().length);
        }

        const { newlyRevealed } = this.syncFilterView();
        if (!newlyRevealed.length) {
          this.updateLoadMoreState();
          return;
        }

        this.collectTriggers();
        this.initImageLoading(this.grid);
        this.refreshFilters();
        this.animateNewItems(newlyRevealed);
        this.refreshLightboxAfterReveal();
        this.updateLoadMoreState();
      } catch (error) {
        this.updateLoadMoreState();
      } finally {
        this.grid.classList.remove('is-loading-batch');
        this.setLoadMoreLoading(false);
      }
    }

    async fetchOlderPage() {
      const page = this.lowestFetchedPage - 1;
      if (page < 1) return 0;

      let appended = 0;

      try {
        const html = await this.fetchSectionPage(page);
        const gallery = this.parseGalleryFromHtml(html);
        const candidates = gallery
          ? [...gallery.querySelectorAll('[data-gallery-item]')]
          : [];

        const fragment = document.createDocumentFragment();
        candidates.forEach((item) => {
          const photoId = this.getPhotoId(item);
          if (photoId && this.knownPhotoIds.has(photoId)) return;
          if (photoId) this.knownPhotoIds.add(photoId);

          // Newly fetched (older) photos start hidden; loadMore reveals them.
          item.setAttribute('hidden', '');
          item.setAttribute('data-gallery-deferred', '');
          item.classList.add('wd-gallery__item--deferred', 'is-reveal-pending');
          item.classList.remove('is-revealed');

          if (this.activeFilter && this.activeFilter !== 'all') {
            const match = item.dataset.room === this.activeFilter;
            item.classList.toggle('is-hidden', !match);
          }

          fragment.appendChild(item);
          appended += 1;
        });

        this.grid.appendChild(fragment);

        // Keep the whole grid in strict newest-to-oldest order after merging
        // the fetched page, in case its timestamps overlap the loaded ones.
        if (appended > 0) this.sortItems();
      } catch (error) {
        appended = 0;
      }

      // Mark this page consumed regardless, so a failed/empty page doesn't loop.
      this.lowestFetchedPage = page;
      return appended;
    }

    parseGalleryFromHtml(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      let gallery = doc.querySelector('wd-customer-gallery');
      if (!gallery) {
        const wrapper =
          doc.querySelector(`#shopify-section-${this.sectionId}`) ||
          doc.querySelector('[id^="shopify-section-"]');
        gallery = wrapper?.querySelector('wd-customer-gallery') || wrapper;
      }
      return gallery;
    }

    async fetchSectionPage(page) {
      const candidateIds = [this.sectionId].filter(Boolean);
      const wrapperId = this.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '');
      if (wrapperId && !candidateIds.includes(wrapperId)) candidateIds.push(wrapperId);

      let lastError = null;
      for (const sectionId of candidateIds) {
        const url = new URL(window.location.pathname, window.location.origin);
        url.searchParams.set('section_id', sectionId);
        url.searchParams.set('page', String(page));

        try {
          const response = await fetch(url.toString(), {
            headers: { Accept: 'text/html' },
            credentials: 'same-origin',
          });
          if (!response.ok) {
            lastError = new Error(`Section fetch failed: ${response.status}`);
            continue;
          }
          const html = await response.text();
          const gallery = this.parseGalleryFromHtml(html);
          if (gallery?.querySelector('[data-gallery-item]')) {
            this.sectionId = sectionId;
            return html;
          }
        } catch (error) {
          lastError = error;
        }
      }
      throw lastError || new Error('Section fetch failed');
    }

    refreshLightboxAfterReveal() {
      if (this.lightbox.hasAttribute('hidden')) return;

      const visible = this.lightboxTriggers();
      const activeTrigger = visible[this.currentIndex];
      this.buildThumbnails(visible);
      const newIndex = activeTrigger ? visible.indexOf(activeTrigger) : this.currentIndex;
      this.currentIndex = Math.max(0, newIndex);
      this.lightboxCounterTotal.textContent = String(visible.length);
      this.updateThumbnailState();
      this.preloadAdjacent(visible, this.currentIndex);
    }

    preloadImage(src) {
      if (!src) return Promise.resolve(null);

      const cached = this.preloadCache.get(src);
      if (cached instanceof HTMLImageElement && cached.complete && cached.naturalWidth > 0) {
        return Promise.resolve(cached);
      }
      if (cached instanceof Promise) return cached;

      const promise = new Promise((resolve) => {
        const img = new Image();
        img.decoding = 'async';

        const finish = () => {
          this.preloadCache.set(src, img);
          resolve(img);
        };

        img.onload = finish;
        img.onerror = finish;
        img.src = src;

        if (img.complete) finish();
      });

      this.preloadCache.set(src, promise);
      return promise;
    }

    preloadAdjacent(visible, index) {
      if (!visible.length) return;

      const len = visible.length;
      const indices = new Set([index]);

      for (let offset = 1; offset <= PRELOAD_AHEAD; offset += 1) {
        indices.add((index - offset + len) % len);
        indices.add((index + offset) % len);
      }

      indices.forEach((i) => {
        const src = visible[i]?.dataset.fullSrc;
        if (src) this.preloadImage(src);
      });
    }

    isImageReady(src) {
      const cached = this.preloadCache.get(src);
      return cached instanceof HTMLImageElement && cached.complete && cached.naturalWidth > 0;
    }

    initImageZoom() {
      if (
        !this.canImageZoom ||
        !this.lightboxImageWrap ||
        !this.lightboxFrame ||
        !this.lightboxImage
      ) {
        return;
      }

      this._onImagePanMove = (event) => {
        if (!this.isPanningImage) return;
        event.preventDefault();
        const movedX = Math.abs(event.clientX - this.panPointerX);
        const movedY = Math.abs(event.clientY - this.panPointerY);
        if (movedX > 4 || movedY > 4) {
          this.didPanImage = true;
        }
        const limits = this.getImagePanLimits();
        this.imagePanX = this.clamp(
          this.panStartX + (event.clientX - this.panPointerX),
          -limits.x,
          limits.x
        );
        this.imagePanY = this.clamp(
          this.panStartY + (event.clientY - this.panPointerY),
          -limits.y,
          limits.y
        );
        this.applyImageTransform();
      };

      this._onImagePanEnd = () => {
        if (!this.isPanningImage) return;
        this.isPanningImage = false;
        this.lightboxFrame.classList.remove('is-dragging');
        document.removeEventListener('mousemove', this._onImagePanMove);
        document.removeEventListener('mouseup', this._onImagePanEnd);
      };

      this.lightboxImageWrap.addEventListener('click', (event) => {
        event.stopPropagation();
        if (this.didPanImage) {
          this.didPanImage = false;
          return;
        }
        this.setImageZoom(!this.isImageZoomed);
      });

      this.lightboxImageWrap.addEventListener('mousedown', (event) => {
        if (!this.isImageZoomed || event.button !== 0) return;
        event.preventDefault();
        this.didPanImage = false;
        this.isPanningImage = true;
        this.panStartX = this.imagePanX;
        this.panStartY = this.imagePanY;
        this.panPointerX = event.clientX;
        this.panPointerY = event.clientY;
        this.lightboxFrame.classList.add('is-dragging');
        document.addEventListener('mousemove', this._onImagePanMove);
        document.addEventListener('mouseup', this._onImagePanEnd);
      });

      this.lightboxImageWrap.addEventListener(
        'touchstart',
        (event) => {
          if (!this.isImageZoomed || event.touches.length !== 1) return;
          const touch = event.touches[0];
          this.didPanImage = false;
          this.isPanningImage = true;
          this.panStartX = this.imagePanX;
          this.panStartY = this.imagePanY;
          this.panPointerX = touch.clientX;
          this.panPointerY = touch.clientY;
          this.lightboxFrame.classList.add('is-dragging');
        },
        { passive: true }
      );

      this.lightboxImageWrap.addEventListener(
        'touchmove',
        (event) => {
          if (!this.isPanningImage || !this.isImageZoomed || event.touches.length !== 1) return;
          const touch = event.touches[0];
          const movedX = Math.abs(touch.clientX - this.panPointerX);
          const movedY = Math.abs(touch.clientY - this.panPointerY);
          if (movedX > 4 || movedY > 4) {
            this.didPanImage = true;
          }
          const limits = this.getImagePanLimits();
          this.imagePanX = this.clamp(
            this.panStartX + (touch.clientX - this.panPointerX),
            -limits.x,
            limits.x
          );
          this.imagePanY = this.clamp(
            this.panStartY + (touch.clientY - this.panPointerY),
            -limits.y,
            limits.y
          );
          this.applyImageTransform();
        },
        { passive: true }
      );

      this.lightboxImageWrap.addEventListener('touchend', () => {
        this.isPanningImage = false;
        this.lightboxFrame.classList.remove('is-dragging');
      });

      this.lightboxImageWrap.addEventListener('dragstart', (event) => {
        event.preventDefault();
      });
    }

    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }

    getImagePanLimits() {
      const image = this.lightboxImage;
      const wrap = this.lightboxImageWrap;
      if (!image || !wrap) return { x: 0, y: 0 };

      const baseWidth = image.offsetWidth;
      const baseHeight = image.offsetHeight;
      if (!baseWidth || !baseHeight) return { x: 0, y: 0 };

      const scaledWidth = baseWidth * this.imageZoomScale;
      const scaledHeight = baseHeight * this.imageZoomScale;

      return {
        x: Math.max(0, (scaledWidth - baseWidth) / 2),
        y: Math.max(0, (scaledHeight - baseHeight) / 2),
      };
    }

    applyImageTransform() {
      if (!this.lightboxImage) return;
      const scale = this.isImageZoomed ? this.imageZoomScale : 1;
      const transform = `translate(${this.imagePanX}px, ${this.imagePanY}px) scale(${scale})`;
      this.lightboxImage.style.transform = transform;
      if (this.lightboxImageAlt && !this.lightboxImageAlt.hidden) {
        this.lightboxImageAlt.style.transform = transform;
      }
    }

    setImageZoom(enabled) {
      this.isImageZoomed = Boolean(enabled);
      this.lightboxFrame?.classList.toggle('is-zoomed', this.isImageZoomed);
      if (!this.isImageZoomed) {
        this.imagePanX = 0;
        this.imagePanY = 0;
      }
      this.applyImageTransform();
    }

    resetImageZoom() {
      this._onImagePanEnd?.();
      this.isImageZoomed = false;
      this.imagePanX = 0;
      this.imagePanY = 0;
      this.lightboxFrame?.classList.remove('is-zoomed', 'is-dragging');
      if (this.lightboxImage) {
        this.lightboxImage.style.transform = '';
      }
      if (this.lightboxImageAlt) {
        this.lightboxImageAlt.style.transform = '';
      }
    }

    bindEvents() {
      this.grid?.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-gallery-open]');
        if (!trigger) return;
        event.preventDefault();
        const visible = this.lightboxTriggers();
        const index = visible.indexOf(trigger);
        if (index >= 0) this.openLightbox(index);
      });

      this.filtersRoot?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-filter]');
        if (!button) return;
        this.applyFilter(button.dataset.filter);
      });

      this.lightboxBackdrop?.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeLightbox();
      });

      this.lightboxShell?.addEventListener('click', (event) => {
        event.stopPropagation();
      });

      this.lightboxClose.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.closeLightbox();
        });
      });

      this.lightboxPrev.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.step(-1);
        });
      });

      this.lightboxNext.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.step(1);
        });
      });

      this.lightbox.addEventListener('touchstart', (event) => {
        this.touchStartX = event.changedTouches[0].screenX;
      }, { passive: true });

      this.lightbox.addEventListener('touchend', (event) => {
        if (this.isImageZoomed) return;
        const delta = event.changedTouches[0].screenX - this.touchStartX;
        if (Math.abs(delta) < 40) return;
        this.step(delta > 0 ? -1 : 1);
      }, { passive: true });

      this._onKeydown = (event) => {
        if (this.lightbox.hasAttribute('hidden')) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          if (this.isImageZoomed) {
            this.resetImageZoom();
            return;
          }
          this.closeLightbox();
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        }
      };

      document.addEventListener('keydown', this._onKeydown);
    }

    async applyFilter(filter) {
      this.activeFilter = filter;
      this.filtersRoot?.querySelectorAll('[data-filter]').forEach((button) => {
        button.classList.toggle('is-active', button.dataset.filter === filter);
      });

      // Token guards against rapid filter switching: if a newer filter is
      // chosen while we're fetching older pages, we abandon the stale one.
      const token = (this._filterToken += 1);

      if (filter === 'all') {
        // "All" resets to the standard first page of newest photos.
        this.visibleCount = Math.min(this.batchSize, this.galleryItems().length);
      } else {
        // Reset the filtered view to its first batch (15) and make sure at
        // least that many matching photos are loaded, pulling older server
        // pages if needed so every filter can reach its minimum of 15.
        this.filterVisibleCount = this.filterBatchSize;
        if (
          this.hasUnfetchedPages() &&
          this.matchingItems(filter).length < this.filterVisibleCount
        ) {
          this.setLoadMoreLoading(true);
          this.grid.classList.add('is-loading-batch');
          try {
            await this.ensureMatchingLoaded(this.filterVisibleCount, token);
          } finally {
            this.grid.classList.remove('is-loading-batch');
            this.setLoadMoreLoading(false);
          }
        }
      }

      // A newer filter was selected while fetching — let that one win.
      if (token !== this._filterToken) return;

      const { newlyRevealed } = this.syncFilterView();
      this.collectTriggers();
      this.initImageLoading(this.grid);
      this.animateNewItems(newlyRevealed);
      this.updateLoadMoreState();

      if (!this.lightbox.hasAttribute('hidden')) {
        const visible = this.lightboxTriggers();
        if (!visible.length) {
          this.closeLightbox();
          return;
        }
        if (this.currentIndex >= visible.length) {
          this.currentIndex = visible.length - 1;
        }
        this.buildThumbnails(visible);
        this.showSlide(visible, this.currentIndex, { isStep: true });
      }
    }

    // All currently loaded items matching a filter, in newest-first DOM order.
    matchingItems(filter = this.activeFilter) {
      const items = this.galleryItems();
      if (filter === 'all') return items;
      return items.filter((item) => item.dataset.room === filter);
    }

    // Pull older server pages until we have at least `targetCount` matching
    // items loaded, or we run out of pages. `token` lets a newer filter abort.
    async ensureMatchingLoaded(targetCount, token) {
      let count = this.matchingItems().length;
      while (count < targetCount && this.hasUnfetchedPages()) {
        const added = await this.fetchOlderPage();
        if (token !== undefined && token !== this._filterToken) return;
        if (!added) break;
        count = this.matchingItems().length;
      }
    }

    // Single source of truth for visibility. Reveals the newest `limit` items
    // matching the active filter, defers matching overflow (still reachable in
    // the lightbox), and fully hides non-matching items. Returns the items that
    // became newly visible so they can be animated in once.
    syncFilterView() {
      const filter = this.activeFilter;
      const limit = filter === 'all' ? this.visibleCount : this.filterVisibleCount;
      const items = this.galleryItems();
      const newlyRevealed = [];
      let shown = 0;

      items.forEach((item) => {
        const match = filter === 'all' || item.dataset.room === filter;

        if (!match) {
          item.classList.add('is-hidden');
          return;
        }

        item.classList.remove('is-hidden');

        const isVisible =
          !item.hasAttribute('hidden') &&
          !item.classList.contains('wd-gallery__item--deferred');

        if (shown < limit) {
          if (!isVisible) {
            item.removeAttribute('hidden');
            item.removeAttribute('data-gallery-deferred');
            item.classList.remove('wd-gallery__item--deferred');
            newlyRevealed.push(item);
          }
          shown += 1;
        } else if (isVisible) {
          item.setAttribute('hidden', '');
          item.setAttribute('data-gallery-deferred', '');
          item.classList.add('wd-gallery__item--deferred');
        }
      });

      return { newlyRevealed, shownCount: shown };
    }

    portalLightbox() {
      if (this.lightbox.parentElement !== document.body) {
        document.body.appendChild(this.lightbox);
      }
    }

    getThumbSrc(trigger) {
      const img = trigger.querySelector('img');
      return img?.currentSrc || img?.src || trigger.dataset.fullSrc || '';
    }

    createThumbButton(trigger, index) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'wd-gallery-lightbox__thumb';
      button.dataset.lightboxThumb = String(index);
      button.setAttribute('aria-label', `View photo ${index + 1}`);

      const thumb = document.createElement('img');
      thumb.dataset.src = this.getThumbSrc(trigger);
      thumb.alt = '';
      thumb.loading = 'lazy';
      thumb.decoding = 'async';
      button.appendChild(thumb);

      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openLightbox(index);
      });

      return button;
    }

    loadThumbImage(img) {
      const src = img.dataset.src;
      if (!src || img.src === src) return;
      img.src = src;
      img.removeAttribute('data-src');
    }

    initThumbLazyLoad() {
      if (!this.lightboxThumbsTrack) return;

      this._thumbObserver?.disconnect();
      this._thumbObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const img = entry.target;
            this.loadThumbImage(img);
            this._thumbObserver.unobserve(img);
          });
        },
        { root: this.lightboxThumbsTrack, rootMargin: '80px 0px' }
      );

      this.lightboxThumbsTrack.querySelectorAll('img[data-src]').forEach((img) => {
        this._thumbObserver.observe(img);
      });
    }

    renderAllThumbs(visible) {
      this.lightboxThumbsTrack.innerHTML = '';
      this.thumbButtons = [];

      const fragment = document.createDocumentFragment();
      visible.forEach((trigger, index) => {
        const button = this.createThumbButton(trigger, index);
        fragment.appendChild(button);
        this.thumbButtons.push(button);
      });
      this.lightboxThumbsTrack.appendChild(fragment);
      this.initThumbLazyLoad();
    }

    renderThumbWindow() {
      const visible = this._visibleList;
      const start = Math.max(0, this.currentIndex - THUMB_WINDOW_RADIUS);
      const end = Math.min(visible.length, this.currentIndex + THUMB_WINDOW_RADIUS + 1);

      this._thumbWindowStart = start;
      this._thumbWindowEnd = end;

      this.lightboxThumbsTrack.innerHTML = '';
      this.thumbButtons = new Array(visible.length).fill(null);

      const fragment = document.createDocumentFragment();

      if (start > 0) {
        const spacerStart = document.createElement('span');
        spacerStart.className = 'wd-gallery-lightbox__thumb-spacer';
        spacerStart.style.width = `${start * 82}px`;
        spacerStart.setAttribute('aria-hidden', 'true');
        fragment.appendChild(spacerStart);
      }

      for (let index = start; index < end; index += 1) {
        const button = this.createThumbButton(visible[index], index);
        fragment.appendChild(button);
        this.thumbButtons[index] = button;
      }

      if (end < visible.length) {
        const spacerEnd = document.createElement('span');
        spacerEnd.className = 'wd-gallery-lightbox__thumb-spacer';
        spacerEnd.style.width = `${(visible.length - end) * 82}px`;
        spacerEnd.setAttribute('aria-hidden', 'true');
        fragment.appendChild(spacerEnd);
      }

      this.lightboxThumbsTrack.appendChild(fragment);
      this.initThumbLazyLoad();
    }

    buildThumbnails(visible) {
      if (!this.lightboxThumbsTrack) return;

      this._visibleList = visible;
      this.thumbButtons = [];

      if (visible.length <= 1) {
        this.lightboxThumbs.hidden = true;
        this.lightboxThumbsTrack.innerHTML = '';
        return;
      }

      this.lightboxThumbs.hidden = false;
      this._useWindowedThumbs = visible.length > THUMB_WINDOW_THRESHOLD;

      if (this._useWindowedThumbs) {
        this.renderThumbWindow();
      } else {
        this.renderAllThumbs(visible);
      }
    }

    updateThumbnailState() {
      if (this._useWindowedThumbs) {
        const outsideWindow =
          this.currentIndex < this._thumbWindowStart || this.currentIndex >= this._thumbWindowEnd;
        if (outsideWindow) {
          this.renderThumbWindow();
        }
      }

      this.thumbButtons.forEach((button, index) => {
        if (!button) return;
        const isActive = index === this.currentIndex;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      const activeThumb = this.thumbButtons[this.currentIndex];
      if (!activeThumb || !this.lightboxThumbsTrack) return;

      const track = this.lightboxThumbsTrack;
      const thumbLeft = activeThumb.offsetLeft;
      const thumbWidth = activeThumb.offsetWidth;
      const trackWidth = track.clientWidth;
      const targetScroll = thumbLeft - trackWidth / 2 + thumbWidth / 2;

      track.scrollTo({
        left: Math.max(0, targetScroll),
        behavior: 'auto',
      });
    }

    updateLightboxCaption(caption, room) {
      const captionParts = [room, caption].filter(Boolean);
      if (captionParts.length) {
        this.lightboxCaption.textContent = captionParts.join(' — ');
        this.lightboxCaption.hidden = false;
      } else {
        this.lightboxCaption.textContent = '';
        this.lightboxCaption.hidden = true;
      }
    }

    swapLightboxImage(src, alt, instant) {
      const incoming = this.lightboxImageAlt;
      const outgoing = this.lightboxImage;

      if (!incoming || !outgoing) {
        outgoing.src = src;
        outgoing.alt = alt;
        return;
      }

      incoming.removeAttribute('hidden');
      incoming.classList.add('is-active');
      incoming.src = src;
      incoming.alt = alt;
      incoming.removeAttribute('width');
      incoming.removeAttribute('height');
      incoming.style.transform = outgoing.style.transform || '';

      outgoing.classList.remove('is-active');
      outgoing.hidden = true;

      this.lightboxImage = incoming;
      this.lightboxImageAlt = outgoing;
      this.activeImageSlot = this.activeImageSlot === 'a' ? 'b' : 'a';

      if (!instant) {
        this.lightbox.classList.add('is-changing');
        window.clearTimeout(this._changingTimer);
        this._changingTimer = window.setTimeout(() => {
          this.lightbox.classList.remove('is-changing');
        }, 180);
      }
    }

    async showSlide(visible, index, options = {}) {
      if (!visible.length) return;

      const normalizedIndex = ((index % visible.length) + visible.length) % visible.length;
      const trigger = visible[normalizedIndex];
      const src = trigger.dataset.fullSrc;
      const caption = trigger.dataset.caption;
      const room = trigger.dataset.room;
      const alt = caption || room || 'Customer installation photo';
      const isAlreadyOpen = !this.lightbox.hasAttribute('hidden');
      const isStep = Boolean(options.isStep);

      this.currentIndex = normalizedIndex;
      this.resetImageZoom();

      if (!this.isImageReady(src)) {
        this.lightbox.classList.add('is-loading-image');
        await this.preloadImage(src);
        this.lightbox.classList.remove('is-loading-image');
      }

      this.swapLightboxImage(src, alt, isStep && this.isImageReady(src));

      this.updateLightboxCaption(caption, room);
      this.lightboxCounterCurrent.textContent = String(this.currentIndex + 1);
      this.lightboxCounterTotal.textContent = String(visible.length);

      const showNav = visible.length > 1;
      this.lightboxPrev.forEach((button) => {
        button.classList.toggle('is-hidden', !showNav);
        button.disabled = !showNav;
      });
      this.lightboxNext.forEach((button) => {
        button.classList.toggle('is-hidden', !showNav);
        button.disabled = !showNav;
      });

      this.updateThumbnailState();
      this.preloadAdjacent(visible, this.currentIndex);

      if (!isAlreadyOpen) {
        this.lightbox.removeAttribute('hidden');
        this.lightbox.setAttribute('aria-hidden', 'false');
        this.lightbox.classList.add('is-open');
        document.documentElement.classList.add('wd-gallery-lock');
        document.body.classList.add('wd-gallery-lock');
        this.lightbox.querySelector('.wd-gallery-lightbox__close')?.focus();
      }
    }

    openLightbox(index, options = {}) {
      const visible = this.lightboxTriggers();
      if (!visible.length) return;

      const isAlreadyOpen = !this.lightbox.hasAttribute('hidden');
      const rebuildThumbs = options.rebuildThumbs ?? !isAlreadyOpen;

      this.portalLightbox();

      if (rebuildThumbs) {
        this.buildThumbnails(visible);
      }

      this.showSlide(visible, index, { isStep: isAlreadyOpen });
    }

    closeLightbox() {
      this.resetImageZoom();
      this.lightbox.setAttribute('hidden', '');
      this.lightbox.setAttribute('aria-hidden', 'true');
      this.lightbox.classList.remove('is-open', 'is-changing', 'is-loading-image');
      document.documentElement.classList.remove('wd-gallery-lock');
      document.body.classList.remove('wd-gallery-lock');

      if (this.lightboxImage) {
        this.lightboxImage.removeAttribute('src');
        this.lightboxImage.classList.add('is-active');
        this.lightboxImage.removeAttribute('hidden');
      }
      if (this.lightboxImageAlt) {
        this.lightboxImageAlt.removeAttribute('src');
        this.lightboxImageAlt.classList.remove('is-active');
        this.lightboxImageAlt.hidden = true;
      }

      this.activeImageSlot = 'a';
      this.lightboxImage = this.lightboxImageA;
      this.lightboxImageAlt = this.lightboxImageB;
    }

    async step(direction) {
      let visible = this.lightboxTriggers();
      if (!visible.length) return;

      // Moving forward past the last loaded photo: pull the next older server
      // page so the lightbox can browse the entire gallery, not just the page.
      const atEnd = direction > 0 && this.currentIndex >= visible.length - 1;
      if (atEnd && this.hasUnfetchedPages() && !this.isFetchingForLightbox) {
        const currentTrigger = visible[this.currentIndex];
        this.isFetchingForLightbox = true;
        this.lightbox.classList.add('is-loading-image');
        try {
          await this.fetchOlderPage();
        } catch (error) {
          /* ignore — fall back to wrapping */
        } finally {
          this.lightbox.classList.remove('is-loading-image');
          this.isFetchingForLightbox = false;
        }
        visible = this.lightboxTriggers();
        // fetchOlderPage re-sorts the grid by date, so re-derive the index of
        // the photo currently on screen before stepping from it.
        const reIndex = visible.indexOf(currentTrigger);
        if (reIndex >= 0) this.currentIndex = reIndex;
        this.buildThumbnails(visible);
      }

      this.openLightbox(this.currentIndex + direction, { rebuildThumbs: false });
    }

    disconnectedCallback() {
      this._onImagePanEnd?.();
      document.removeEventListener('keydown', this._onKeydown);
      if (this._onStickyOffsetChange) {
        window.removeEventListener('scroll', this._onStickyOffsetChange);
        window.removeEventListener('resize', this._onStickyOffsetChange);
      }
      this._revealObserver?.disconnect();
      this._mediaObserver?.disconnect();
      this._thumbObserver?.disconnect();
      window.clearTimeout(this._changingTimer);
      window.clearTimeout(this._batchCleanupTimer);
      document.documentElement.classList.remove('wd-gallery-lock');
      document.body.classList.remove('wd-gallery-lock');
    }
  }

  customElements.define('wd-customer-gallery', WDCustomerGallery);
})();
